import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, MessageSquare, Send, CheckCircle2, 
  Clock, Paperclip, Sparkles, Building2, Calendar, 
  Volume2, VolumeX, Play, Pause, ChevronRight, X, 
  HelpCircle, FileText, Check, AlertCircle, Download,
  Inbox, Megaphone, AlertTriangle
} from 'lucide-react';

export const FacultyMessagingDesk = ({ 
  facilitator, 
  currentModule, 
  course, 
  student,
  isModal = false,
  onClose = null 
}) => {
  const [selectedTopic, setSelectedTopic] = useState('methodology');
  const [messageText, setMessageText] = useState('');
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [copiedContact, setCopiedContact] = useState(null);
  const [isSimulatingFeedback, setIsSimulatingFeedback] = useState(false);

  const [activeUrgentAlert, setActiveUrgentAlert] = useState(() => {
    try {
      const stored = localStorage.getItem('aksu_urgent_inbox_alert');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return null;
  });

  useEffect(() => {
    const syncAlert = () => {
      try {
        const stored = localStorage.getItem('aksu_urgent_inbox_alert');
        if (stored) setActiveUrgentAlert(JSON.parse(stored));
      } catch (e) {}
    };

    const handleAlertChange = (e) => {
      if (e.detail) setActiveUrgentAlert(e.detail);
      else syncAlert();
    };

    window.addEventListener('storage', syncAlert);
    window.addEventListener('aksu_urgent_alert_change', handleAlertChange);
    return () => {
      window.removeEventListener('storage', syncAlert);
      window.removeEventListener('aksu_urgent_alert_change', handleAlertChange);
    };
  }, []);

  const handleAcknowledgeAlert = () => {
    if (!activeUrgentAlert) return;
    const updated = {
      ...activeUrgentAlert,
      isBlinking: false,
      acknowledged: true,
      acknowledgedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setActiveUrgentAlert(updated);
    try {
      localStorage.setItem('aksu_urgent_inbox_alert', JSON.stringify(updated));
    } catch (e) {}
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('aksu_urgent_alert_change', { detail: updated }));
    }
  };

  // Default conversation history between Candidate and this specific Facilitator
  const [conversation, setConversation] = useState([
    {
      id: 'msg-1',
      sender: 'student',
      senderName: student?.fullName || 'Emem Udoh',
      avatar: student?.avatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=300&auto=format&fit=crop',
      time: 'Yesterday, 14:15',
      subject: `Inquiry on ${currentModule?.title || 'Methodological Grounding'}`,
      text: `Good afternoon ${facilitator?.name || 'Professor'}. While reviewing the lecture materials for ${currentModule?.number || 'this module'}, I am evaluating whether Critical Realism is acceptable for my executive DBA dilemma regarding downstream oil logistics in Akwa Ibom State, rather than pure Positivism. Does your department encourage a mixed ontological stance for CA1?`,
      status: 'Read by Professor'
    },
    {
      id: 'msg-2',
      sender: 'faculty',
      senderName: facilitator?.name || 'Prof. Asuquo E. Johnson, PhD',
      senderTitle: facilitator?.rank || 'Lead Professor of Strategic Management',
      avatar: facilitator?.avatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=300&auto=format&fit=crop',
      time: 'Yesterday, 16:40',
      subject: `RE: Inquiry on ${currentModule?.title || 'Methodological Grounding'}`,
      text: `Dear Scholar ${student?.fullName?.split(' ')[0] || 'Udoh'},\n\nAn insightful inquiry. Critical Realism is not only acceptable; it is strongly recommended for executive doctoral research where complex socio-technical and regulatory variables interact. In your CA1 submission, ensure you clearly differentiate between the empirical (observable logistics bottlenecks), the actual (supply chain events), and the real (underlying institutional structures).\n\nI have uploaded supplementary HBR case notes to your Visual Guide. Feel free to raise this during our Saturday executive seminar.`,
      status: 'Official Academic Feedback',
      hasAudioNote: true,
      audioDuration: '0:42'
    }
  ]);

  const quickPrompts = [
    { label: "Clarify Ontological Stance", text: "Could you clarify the distinction between Realism and Nominalism as applied to strategic corporate leadership?" },
    { label: "CA1 Submission Review", text: "I have prepared a draft of my Continuous Assessment 1 matrix and would appreciate preliminary feedback before final submission." },
    { label: "Book Office Hours Consultation", text: "I would like to reserve a 15-minute consultation slot during your upcoming executive office hours." }
  ];

  const handleSendMessage = (e) => {
    e?.preventDefault();
    if (!messageText.trim()) return;

    const newMsgId = `msg-${Date.now()}`;
    const newStudentMsg = {
      id: newMsgId,
      sender: 'student',
      senderName: student?.fullName || 'Emem Udoh',
      avatar: student?.avatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=300&auto=format&fit=crop',
      time: 'Just now',
      subject: `Inquiry on ${currentModule?.title || 'Coursework'}`,
      text: messageText,
      status: 'Delivered to Faculty Console'
    };

    setConversation(prev => [...prev, newStudentMsg]);
    const sentText = messageText;
    setMessageText('');

    // Trigger faculty feedback simulation
    setIsSimulatingFeedback(true);
    setTimeout(() => {
      let facultyReplyText = `Dear Scholar,\n\nThank you for reaching out regarding ${currentModule?.number || 'this module'}. I have reviewed your submission on "${sentText.substring(0, 50)}...". \n\nYour line of inquiry demonstrates solid academic rigour. In executive business research, grounding your problem statement in verifiable evidence while maintaining actionable relevance is key. Please incorporate Saunders' Onion layer 2 (deductive vs. inductive) to further strengthen this argument.\n\nBest regards,\n${facilitator?.name || 'Prof. Asuquo E. Johnson'}\n${facilitator?.office || 'Faculty Suite, AKSU Business School'}`;

      const facultyReply = {
        id: `fac-reply-${Date.now()}`,
        sender: 'faculty',
        senderName: facilitator?.name || 'Prof. Asuquo E. Johnson, PhD',
        senderTitle: facilitator?.rank || 'Module Facilitator',
        avatar: facilitator?.avatar,
        time: 'Just now',
        subject: `Academic Feedback: ${currentModule?.number || 'Module'} Inquiry`,
        text: facultyReplyText,
        status: 'Official Academic Feedback',
        hasAudioNote: false
      };

      setConversation(prev => [...prev, facultyReply]);
      setIsSimulatingFeedback(false);
    }, 2000);
  };

  const handleCopy = (type, val) => {
    navigator.clipboard?.writeText(val);
    setCopiedContact(type);
    setTimeout(() => setCopiedContact(null), 2000);
  };

  return (
    <div className={`bg-white rounded-3xl border-2 border-slate-200 overflow-hidden shadow-sm flex flex-col ${isModal ? 'h-[85vh] max-w-4xl w-full' : ''}`}>
      
      {/* Top Facilitator Identity Bar */}
      <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white p-5 sm:p-6 border-b-2 border-[#7A470E] flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <img 
              src={facilitator?.avatar || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=300&auto=format&fit=crop"} 
              alt={facilitator?.name} 
              className="w-16 h-16 rounded-2xl object-cover border-2 border-[#B87A28] shadow-md"
            />
            <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white shadow-xs" title="Online & Available" />
          </div>

          <div>
            <div className="flex items-center space-x-2">
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#7A470E] text-white font-bold uppercase tracking-wider">
                Module Facilitator
              </span>
              <span className="text-xs text-emerald-400 font-mono flex items-center space-x-1 font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span>{facilitator?.status || 'Online'}</span>
              </span>
            </div>

            <h3 className="text-lg sm:text-xl font-black font-sc text-white mt-1 leading-tight">
              {facilitator?.name || 'Prof. Asuquo E. Johnson, PhD'}
            </h3>

            <p className="text-xs text-slate-300 font-sc">
              {facilitator?.rank || 'Lead Professor of Strategic Management & Research Methodology'}
            </p>
          </div>
        </div>

        {/* Action Controls & Contact Pills */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Email button */}
          <a
            href={`mailto:${facilitator?.email || 'a.johnson@faculty.aksu.edu.ng'}`}
            className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-mono flex items-center space-x-1.5 transition-colors"
            title="Send Direct Email"
          >
            <Mail className="w-3.5 h-3.5 text-amber-300" />
            <span className="hidden sm:inline">{facilitator?.email || 'faculty@aksu.edu.ng'}</span>
          </a>

          {/* Phone / WhatsApp button */}
          <a
            href={`https://wa.me/${(facilitator?.phone || '+2348037219840').replace(/[^0-9]/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-mono font-bold flex items-center space-x-1.5 transition-colors shadow-xs"
            title="Chat via Official WhatsApp"
          >
            <Phone className="w-3.5 h-3.5" />
            <span>{facilitator?.phone || '+234 803 721 9840'}</span>
          </a>

          {isModal && onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Office & Consultation Info Strip */}
      <div className="bg-amber-50/80 px-5 py-2.5 border-b border-amber-200/80 flex flex-wrap items-center justify-between text-xs text-slate-700 gap-2">
        <div className="flex items-center space-x-2">
          <Building2 className="w-4 h-4 text-[#7A470E] shrink-0" />
          <span className="font-bold text-[#7A470E] font-sc">Office Suite:</span>
          <span>{facilitator?.office || 'Faculty Suite 4B, Senate Academic Wing, Ikot Akpaden'}</span>
        </div>

        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-[#7A470E] shrink-0" />
          <span className="font-bold text-[#7A470E] font-sc">Consultation Hours:</span>
          <span className="font-mono">{facilitator?.consultationHours || 'Thursdays 14:00 - 17:00 & Saturdays'}</span>
        </div>
      </div>

      {/* Module Context Banner */}
      <div className="px-5 sm:px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs">
        <div className="flex items-center space-x-2 truncate">
          <MessageSquare className="w-4 h-4 text-[#7A470E] shrink-0" />
          <span className="font-bold font-sc text-[#0B2644]">Academic Desk Context:</span>
          <span className="truncate text-slate-600 font-mono">
            {course?.code || 'DBA 801'} • {currentModule?.number || 'Lesson 1'}: {currentModule?.title || 'Foundations of Scientific Inquiry'}
          </span>
        </div>

        <span className="text-[11px] font-mono text-emerald-800 bg-emerald-100 font-bold px-2 py-0.5 rounded-full shrink-0">
          In-LMS Verified Channel
        </span>
      </div>

      {/* Conversation Feed */}
      <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4 max-h-[420px] bg-slate-50/50">
        
        {/* ACTIVE CLASS BROADCAST / URGENT ADVISORY PINNED CARD */}
        {activeUrgentAlert && (!activeUrgentAlert.courseCode || activeUrgentAlert.courseCode === course?.code) && (
          <div className={`p-4 rounded-2xl border-2 space-y-2 mb-4 transition-all shadow-sm ${
            activeUrgentAlert.isBlinking && !activeUrgentAlert.acknowledged
              ? 'bg-red-50/95 border-red-500 ring-2 ring-red-400/40'
              : 'bg-amber-50/70 border-amber-300'
          }`}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-2">
              <div className="flex items-center space-x-2">
                <Megaphone className={`w-4 h-4 ${activeUrgentAlert.isBlinking && !activeUrgentAlert.acknowledged ? 'text-red-600 animate-pulse' : 'text-[#7A470E]'}`} />
                <span className="text-xs font-black font-sc text-[#0B2644] uppercase tracking-wider">
                  Official Class Broadcast & Directive
                </span>
              </div>
              {activeUrgentAlert.isBlinking && !activeUrgentAlert.acknowledged ? (
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-red-600 text-white font-mono font-bold animate-pulse">
                  🚨 ATTENTION REQUIRED
                </span>
              ) : (
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-mono font-bold">
                  ✓ Acknowledged
                </span>
              )}
            </div>

            <div>
              <h4 className="text-xs font-bold font-sc text-[#0B2644]">
                {activeUrgentAlert.subject}
              </h4>
              <p className="text-xs text-slate-700 font-sans leading-relaxed whitespace-pre-line mt-1">
                {activeUrgentAlert.message}
              </p>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-200/60 text-[10px] font-mono text-slate-500">
              <span>Dispatched: {activeUrgentAlert.timestamp} · {activeUrgentAlert.senderName}</span>
              {activeUrgentAlert.isBlinking && !activeUrgentAlert.acknowledged && (
                <button
                  onClick={handleAcknowledgeAlert}
                  className="px-3 py-1 bg-emerald-700 hover:bg-emerald-800 text-white font-sc font-bold rounded-lg cursor-pointer transition-colors shadow-2xs"
                >
                  ✓ Acknowledge Receipt
                </button>
              )}
            </div>
          </div>
        )}
        {conversation.map((msg) => {
          const isFaculty = msg.sender === 'faculty';

          return (
            <div 
              key={msg.id} 
              className={`flex items-start space-x-3 ${isFaculty ? '' : 'flex-row-reverse space-x-reverse'}`}
            >
              <img 
                src={msg.avatar} 
                alt={msg.senderName} 
                className="w-10 h-10 rounded-xl object-cover border border-slate-300 shadow-xs shrink-0 mt-1"
              />

              <div className={`max-w-xl rounded-2xl p-4 space-y-2 shadow-xs border ${
                isFaculty 
                  ? 'bg-white border-[#7A470E]/30 text-slate-900 rounded-tl-none' 
                  : 'bg-[#0B2644] text-white border-[#06182B] rounded-tr-none'
              }`}>
                {/* Message Header */}
                <div className="flex items-center justify-between gap-4 border-b pb-1.5 border-slate-200/50">
                  <div>
                    <strong className={`block text-xs font-sc font-black ${isFaculty ? 'text-[#0B2644]' : 'text-amber-300'}`}>
                      {msg.senderName}
                    </strong>
                    {isFaculty && (
                      <span className="text-[10px] text-[#7A470E] font-sc block">
                        {msg.senderTitle}
                      </span>
                    )}
                  </div>
                  <span className={`text-[10px] font-mono ${isFaculty ? 'text-slate-400' : 'text-slate-300'}`}>
                    {msg.time}
                  </span>
                </div>

                {/* Message Body */}
                <p className="text-xs leading-relaxed whitespace-pre-line">
                  {msg.text}
                </p>

                {/* Audio Feedback Snippet if present */}
                {msg.hasAudioNote && (
                  <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between text-xs text-slate-800">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setIsPlayingAudio(!isPlayingAudio)}
                        className="w-8 h-8 rounded-full bg-[#7A470E] text-white flex items-center justify-center hover:scale-105 transition-transform cursor-pointer shadow-xs"
                      >
                        {isPlayingAudio ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                      </button>
                      <div>
                        <span className="font-bold text-[#0B2644] font-sc block text-[11px]">
                          Professor's Audio Feedback Note
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">
                          {isPlayingAudio ? "Playing 0:18 / 0:42..." : `Recorded by Faculty (${msg.audioDuration})`}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-1 text-slate-400">
                      <Volume2 className="w-4 h-4 text-[#7A470E]" />
                    </div>
                  </div>
                )}

                {/* Message Status Tag */}
                <div className="flex items-center justify-between pt-1 text-[10px] font-mono">
                  <span className={`flex items-center space-x-1 ${isFaculty ? 'text-emerald-700 font-bold' : 'text-slate-300'}`}>
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    <span>{msg.status}</span>
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Simulating Faculty Feedback Indicator */}
        {isSimulatingFeedback && (
          <div className="flex items-center space-x-3 animate-in fade-in">
            <img 
              src={facilitator?.avatar} 
              alt={facilitator?.name} 
              className="w-10 h-10 rounded-xl object-cover border border-[#7A470E] shadow-xs"
            />
            <div className="p-3.5 rounded-2xl bg-white border border-[#7A470E]/40 text-xs text-slate-700 shadow-xs flex items-center space-x-2">
              <span className="w-2 h-2 rounded-full bg-[#7A470E] animate-ping" />
              <span className="font-sc font-bold text-[#0B2644]">{facilitator?.name} is reviewing your inquiry & typing feedback...</span>
            </div>
          </div>
        )}
      </div>

      {/* Suggested Quick Inquiry Chips */}
      <div className="px-5 py-2.5 bg-slate-100/70 border-t border-slate-200 flex items-center space-x-2 overflow-x-auto text-xs">
        <span className="text-[10px] font-bold uppercase font-mono text-slate-500 shrink-0">
          Suggested Inquiries:
        </span>
        {quickPrompts.map((p, idx) => (
          <button
            key={idx}
            onClick={() => setMessageText(p.text)}
            className="px-2.5 py-1 rounded-lg bg-white hover:bg-amber-50 text-slate-700 hover:text-[#7A470E] border border-slate-200 text-[11px] font-sc font-bold whitespace-nowrap cursor-pointer transition-colors shadow-2xs"
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Message Input Box */}
      <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-slate-200 space-y-3">
        <div className="relative">
          <textarea
            rows="3"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder={`Message ${facilitator?.name || 'your Course Facilitator'} directly for guidance on this module...`}
            className="w-full p-3.5 pr-24 rounded-2xl border-2 border-slate-200 focus:border-[#7A470E] focus:outline-none text-xs sm:text-sm text-slate-800 placeholder-slate-400 bg-slate-50/50 resize-none"
          />

          <div className="absolute right-3 bottom-3 flex items-center space-x-1.5">
            <button
              type="submit"
              disabled={!messageText.trim()}
              className="px-4 py-2 rounded-xl bg-[#0B2644] hover:bg-[#7A470E] disabled:bg-slate-300 text-white font-bold text-xs font-sc flex items-center space-x-1.5 transition-all shadow-xs cursor-pointer disabled:cursor-not-allowed"
            >
              <span>Send</span>
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between text-[11px] text-slate-500 px-1">
          <span className="flex items-center space-x-1">
            <Sparkles className="w-3.5 h-3.5 text-[#7A470E]" />
            <span>Messages are logged into candidate academic record & copied to facilitator email.</span>
          </span>
          <span className="font-mono text-emerald-700 font-bold">
            Average Response: &lt; 2 Hours
          </span>
        </div>
      </form>

    </div>
  );
};
